
import React, { useEffect, useState } from 'react'

import useTranslation from '@/Hooks/UseTranslation'

import ToggleGroupIcon from '../../../AnimatedIcons/ToggleGroupIcon/ToggleGroupIcon'
import DocumentIcon from './DocumentIcon'

import Download from '@/Components/SvgImages/Download'
import Spinner from '@/Components/SvgImages/Spinner/Spinner'
import TrashIcon from '@/Components/SvgImages/TrashIcon'

import './DocumentItem.scss'
import { Col, FormGroup, FormLabel, Row } from 'react-bootstrap'
import InputText from '../../components/InputText'
import FormSavingStatus from '../../components/FormSavingStatus/FormSavingStatus'
import { useDispatch, useSelector } from 'react-redux'
import UserAvatar from '@/Components/UserAvatar/UserAvatar'
import { deleteDocument, downloadDocument, updateDocument } from '@/Services/ProgrammationManagerService'
import { useProgrammation } from '@/Hooks/UseProgrammation'
import { deleteDocumentAction, updateDocumentAction } from '@/Redux/Actions/ProgrammationManagerActions'
import moment from 'moment'
import axios from 'axios'

interface Props {
  document: any
  canDelete?: boolean
}

const truncateFilename = (filename: string, maxLength = 26) => {
  const lastDotIndex = filename.lastIndexOf('.');
  if (lastDotIndex === -1) return filename;

  const name = filename.substring(0, lastDotIndex);
  const ext = filename.substring(lastDotIndex);

  if (filename.length <= maxLength) return filename;

  const visibleChars = maxLength - ext.length - 3;
  const head = name.substring(0, Math.ceil(visibleChars / 2));
  const tail = name.substring(name.length - Math.floor(visibleChars / 2));

  return `${head}...${tail}${ext}`;
};

function formatFileSize(bytes: number): string {
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;

  while (bytes >= 1024 && i < units.length - 1) {
    bytes /= 1024;
    i++;
  }

  return `${bytes.toFixed(2)} ${units[i]}`;
}

const DocumentItem = ({ document: taskDocument, canDelete }: Props) => {

  const { t } = useTranslation()

  const dispatch = useDispatch()

  const { itemOpened, patchItemOpened } = useProgrammation()

  const { users } = useSelector((state: any) => state.ProgrammationReducer)

  const [ isCollapsed, setIsCollapsed ] = useState(true)
  const [ saving, setSaving ] = useState(false)
  const [ deleting, setDeleting ] = useState(false)

  const [ user, setUser ] = useState<any>(null)

  const handleDownload = async () => {

    try {
      const [error, res]: any = await downloadDocument(taskDocument._id);

      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const disposition = res.headers['Content-Disposition'];
      const match = disposition?.match(/filename="(.+)"/);
      const fileName = match ? decodeURIComponent(match[1]) : taskDocument.name;

      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error al descargar archivo:', error);
    }
  };

  const handleChangeName = async (name: string) => {

    setSaving(true)

    const [error] = await updateDocument(taskDocument._id, name)

    setSaving(false)

    if (error) {
      return console.log(error)
    }

    patchItemOpened({
      documents: itemOpened.documents.map((d: any) => d._id === taskDocument._id ? { ...d, name } : d)
    })

    await dispatch(updateDocumentAction(taskDocument._id, name))
  }

  const handleDelete = async () => {
    setDeleting(true)

    const [ error, data ] = await deleteDocument(taskDocument._id)

    setDeleting(false)

    if (error) {
      return console.log(error)
    }

    patchItemOpened({
      documents: itemOpened.documents.filter((d: any) => d._id !== taskDocument._id)
    })

    dispatch(deleteDocumentAction(taskDocument._id))
  }

  useEffect(() => {

    const getUser = () => {

      const user = users.find((u: any) => u._id === taskDocument.user)

      if (user) {

        setUser(user)
      }
    }

    getUser()
  }, [ taskDocument.user ])

  return (
    <div className='document-item'>
      <div className="document-item-header">
        <div className="document-item-icon">
          <DocumentIcon mimeType={taskDocument.mimeType} />
        </div>
        <div className="document-item-name">{truncateFilename(taskDocument.name)}</div>
        <div className="document-item-actions">
          <button
            className="document-item-button"
            onClick={handleDownload}
          >
            <Download height={24} width={24} />
          </button>
          <button
            className={`document-item-button ${isCollapsed ? 'collapsed' : ''}`}
            onClick={() => setIsCollapsed(!isCollapsed)}
          >
            <ToggleGroupIcon
              size={20}
              isOpen={!isCollapsed}
            />
          </button>
        </div>
      </div>
      <div className={`document-item-body ${isCollapsed ? 'collapsed' : ''}`}>
        <div className="document-item-body-content">
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '.5rem' }}>
            {saving && (
              <FormSavingStatus saving={saving} />
            )}
            <button
              className="btn btn-danger"
              onClick={handleDelete}
              disabled={deleting}
              >
              {deleting && (
                <>
                  <Spinner
                    height={20}
                    width={20}
                  />
                  {t('DOCUMENT_ITEM:DeleteButton')}
                </>
              )}
              {!deleting && (
                <>
                  <TrashIcon
                    height={20}
                    width={20}
                    color="#e21c1c"
                  />
                  {t('DOCUMENT_ITEM:DeleteButton')}
                </>
              )}
            </button>
          </div>
          <FormGroup>
            <FormLabel>
              {t('DOCUMENT_ITEM:NameLabel')}
            </FormLabel>
            <InputText
              value={taskDocument.name}
              onChange={handleChangeName}
              delay={1000}
            />
          </FormGroup>
          <FormGroup>
            <FormLabel>
              {t('DOCUMENT_ITEM:SizeLabel')}
            </FormLabel>
            <InputText
              value={formatFileSize(taskDocument.size)}
              disabled
            />
          </FormGroup>
          <FormGroup>
            <FormLabel>
              {t('DOCUMENT_ITEM:UploaderLabel')}
            </FormLabel>
            <div className='user-info'>
              <div className="user-avatar">
                <UserAvatar user={user} />
              </div>
              <div className="user-name">
                {user?.name} {user?.surname}
              </div>
            </div>
          </FormGroup>
          <FormGroup>
            <FormLabel>
              {t('DOCUMENT_ITEM:UploadedAtLabel')}
            </FormLabel>
            <InputText
              value={moment(taskDocument.createdAt).format('DD/MM/YYYY h:mm a')}
              disabled
              delay={500}
            />
          </FormGroup>
        </div>
      </div>
    </div>
  )
}

export default DocumentItem
